import { AlgoRerunDto, CentreBase } from '@cambridgeassessment/lib-ee-commons';
import { Entry } from '@cambridgeassessment/lib-ee-commons/src/domain/db';
import moment from 'moment';
import { DATE_DISPLAY_FORMAT_FULL } from '../../../constants';
import { resolveRerunReasonText } from './AlgoRerun/helpers';

export enum SortTypes {
  CandidateNr = 'CANDIDATE_NR',
  Mark = 'MARK',
  Centre = 'CENTRE',
  Date = 'DATE',
  Username = 'USERNAME',
  Reason = 'REASON',
}

export enum SortOrder {
  Descending = 'DESC',
  Ascending = 'ASC',
}

export interface TableSortConfig {
  sortField: SortTypes | undefined;
  order: SortOrder;
}

export const resolveSortOrder = (
  field: TableSortConfig['sortField'],
  setSortConfig: (value: TableSortConfig) => void,
  sortConfig?: TableSortConfig,
): void => {
  let order = SortOrder.Descending;
  if (sortConfig?.sortField === field && sortConfig?.order === SortOrder.Descending) {
    order = SortOrder.Ascending;
  }
  setSortConfig({ order: order, sortField: field });
};

export const sortLearners = (entries: Entry[], sortOptions?: TableSortConfig): Entry[] | undefined => {
  switch (sortOptions?.sortField) {
    case SortTypes.CandidateNr: {
      return entries.sort((a, b) => {
        return sortOptions.order === SortOrder.Ascending
          ? a.learner.number - b.learner.number
          : b.learner.number - a.learner.number;
      });
    }
    case SortTypes.Mark: {
      return entries.sort((a, b) => {
        const aMark = a.mark ?? 0;
        const bMark = b.mark ?? 0;
        return sortOptions?.order === SortOrder.Descending ? bMark - aMark : aMark - bMark;
      });
    }
    case SortTypes.Centre: {
      return entries.sort((a, b) => {
        return sortOptions.order === SortOrder.Descending
          ? a.centreId.localeCompare(b.centreId)
          : b.centreId.localeCompare(a.centreId);
      });
    }

    default: {
      return entries;
    }
  }
};

export const sortAlgoRerunTable = (data: AlgoRerunDto[], sortOptions?: TableSortConfig): AlgoRerunDto[] | undefined => {
  switch (sortOptions?.sortField) {
    case SortTypes.Date: {
      return data.sort((a, b) => {
        const aDate = moment(a.dateOfRerun, DATE_DISPLAY_FORMAT_FULL);
        const bDate = moment(b.dateOfRerun, DATE_DISPLAY_FORMAT_FULL);

        return sortOptions.order === SortOrder.Ascending ? aDate.diff(bDate) : bDate.diff(aDate);
      });
    }
    case SortTypes.Username: {
      return data.sort((a, b) =>
        sortOptions.order === SortOrder.Ascending ? a.user.localeCompare(b.user) : b.user.localeCompare(a.user),
      );
    }
    case SortTypes.Reason: {
      return data.sort((a, b) => {
        const aText = resolveRerunReasonText(a.type);
        const bText = resolveRerunReasonText(b.type);

        return sortOptions.order === SortOrder.Ascending ? aText.localeCompare(bText) : bText.localeCompare(aText);
      });
    }

    default: {
      return data;
    }
  }
};

export const filterLearners = (
  searchValue: string,
  learners: Entry[],
  centreId?: CentreBase['id'],
): Entry[] | undefined => {
  if (searchValue == '') {
    return undefined;
  }
  const searchValueLowercased = searchValue.toLowerCase();
  const filteredLearners = learners.filter(
    (x) =>
      x.learner.number.toString().padStart(4, '0').includes(searchValueLowercased) ||
      x.learner.name.toLowerCase().includes(searchValueLowercased) ||
      (x.mark && x.mark.toString().includes(searchValueLowercased)),
  );

  const filteredLearnersInConsortia = learners.filter(
    (x) =>
      (x.learner.number.toString().padStart(4, '0').includes(searchValueLowercased) ||
        x.learner.name.toLowerCase().includes(searchValueLowercased) ||
        (x.mark && x.mark.toString().includes(searchValueLowercased))) &&
      x.centreId === centreId,
  );

  return centreId ? filteredLearnersInConsortia : filteredLearners;
};
